import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Alert, Card, Col, Container, Form, Row, Spinner, ListGroup } from "react-bootstrap";
import './manageUsers.css';
import { api } from "../../index";
import { DeckRow, type DeckData } from "../decks/decks.tsx";
import { MatchList } from "../matchHistory/MatchHistory.tsx";

type PermissionsToggleButtonProps = {
    name: string;
    value: string;
    userPermissions: string;
    targetUserId: string;
    currentUser: any;
    isOwnerView: boolean;
    onToggle: Function;
}

export type MatchData = {
    match_id: number;
    winner: number;
    loser: number;
    winner_deck_id: number;
    loser_deck_id: number;
    winner_username?: string;
    loser_username?: string;
    winner_deck_name?: string;
    loser_deck_name?: string;
}

const PermissionToggleButton = ({
    name,
    value,
    userPermissions,
    targetUserId,
    currentUser,
    isOwnerView,
    onToggle,
}: PermissionsToggleButtonProps) => {
    const isSelected = userPermissions.includes(value);

    // Determine disabled state based on complex permission rules
    const isDisabled = useMemo(() => {
        // 1. If not an owner, cannot add/remove 'owner' or 'admin' permissions
        if (!isOwnerView && (value === 'a')) {
            return true;
        }

        if (value === 'o') {
            return true;
        }

        // 2. If an owner, cannot remove your own 'owner' permission (to prevent lockout)
        if (isOwnerView && targetUserId === currentUser.user_id && value === 'o') {
            return true;
        }


        return false;
    }, [isOwnerView, value, targetUserId, currentUser]);

    return (
        <Button
            variant={isSelected ? 'primary' : 'outline-secondary'}
            size='sm'
            className='me-1 mb-1' // Add margin to separate buttons
            onClick={() => {
                onToggle(targetUserId, value, isSelected ? 'remove' : 'add')
            }}
            disabled={isDisabled}
            aria-pressed={isSelected}
            aria-label={`${name} permission for user ${targetUserId}`}
        >
            {name}
        </Button>
    );
};

export default function ManageUsersPanel({ owner: isOwnerView }: { owner: boolean }) {
    const [userList, setUserList] = useState<any[]>([]);
    const [nameSearch, setNameSearch] = useState('');
    const [permsSearch, setPermsSearch] = useState(''); // Stores the single permission char to filter by
    const [currentUser, setCurrentUser] = useState<any>(null); // Info about the logged-in user
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [loadingSelf, setLoadingSelf] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false); // For individual permission toggles
    const [x, reRender] = useState(0); // reload permissions
    const [showDecks, setShowDecks] = useState<{ userId: string, username: string } | null>(null); // For individual permission toggles

    // --- Data Fetching ---
    const updateUsers = async () => {
        setLoadingUsers(true);
        setError(null);
        try {
            const res = await api.post(`/admin/users/get`, {}, { withCredentials: true });
            setUserList(res.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to fetch user list.');
        } finally {
            setLoadingUsers(false);
        }
    };

    const getSelfInfo = async () => {
        setLoadingSelf(true);
        setError(null);
        try {
            const res = await api.post('/auth/user', {}, { withCredentials: true });
            setCurrentUser(res.data);
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to fetch current user info.');
        } finally {
            setLoadingSelf(false);
        }
    };

    useEffect(() => {
        updateUsers();
        getSelfInfo();
    }, [x]); // Run once on component mount

    // --- Permission Toggle Logic ---
    const handlePermissionToggle = async (userId: number, permission: string, action: 'add' | 'remove') => {
        setUpdating(true); // Indicate that an update is in progress
        setError(null);
        const url = `/admin/users/${action}Permission`;
        try {
            await api.post(url, { userId, permission }, { withCredentials: true });
            updateUsers(); // Refresh the list after successful change
        } catch (e: any) {
            setError(e.response?.data?.message || `Failed to ${action} permission.`);
        } finally {
            setUpdating(false);
        }
        reRender(x + 1);
    };

    // --- User Deletion Logic ---
    const deleteUser = async (userId: number) => {
        if (currentUser && userId === currentUser.user_id) {
            setError("Cannot delete your own account from this panel.");
            return;
        }
        if (!window.confirm(`Are you sure you want to delete user ID ${userId}?`)) {
            return;
        }
        setUpdating(true); // Indicate update
        setError(null);
        try {
            await api.post(`/admin/users/delete`, { userId }, { withCredentials: true });
            updateUsers(); // Refresh the list
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to delete user.');
        } finally {
            setUpdating(false);
        }
    };

    // --- Filtered and Sorted Users (Memoized for Performance) ---
    const filteredUsers = useMemo(() => {
        if (!currentUser) return []; // Don't filter if current user info isn't loaded

        return userList
            .filter((user) => {
                const matchesName =
                    user.username.toLowerCase().includes(nameSearch.toLowerCase()) ||
                    String(user.user_id).includes(nameSearch);
                const matchesPerms = permsSearch === '' || user.permissions.includes(permsSearch);
                return matchesName && matchesPerms;
            })
            .sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase()));
    }, [userList, nameSearch, permsSearch, currentUser]);

    // --- Permissions for buttons ---
    const allPermissions = [
        { name: 'Owner', value: 'o' },
        { name: 'Admin', value: 'a' },
        { name: 'Spectator', value: 's' },
        { name: 'Player', value: 'p' },
        { name: 'API Tester', value: 't' },
    ];

    // --- Render ---
    if (loadingUsers || loadingSelf || !currentUser) {
        return (
            <Container className='py-5 d-flex flex-column align-items-center justify-content-center flex-grow-1'>
                <Spinner animation='border' role='status' className='mb-3' />
                <p>Loading user data...</p>
            </Container>
        );
    }

    if (showDecks !== null) {
        return <ShowDecksPanel userData={showDecks} setShowDecks={setShowDecks} />
    }

    return (
        <Card className='shadow-sm'>
            <Card.Header as='h4'>Manage Users</Card.Header>
            <Card.Body>
                {error && <Alert variant='danger'>{error}</Alert>}

                <Row className='mb-4 g-3 align-items-center'>
                    <Col md={6}>
                        <Form.Group controlId='userSearch'>
                            <Form.Label className='fw-semibold'>Search by Username or ID</Form.Label>
                            <Form.Control
                                type='text'
                                placeholder='Search users...'
                                value={nameSearch}
                                onChange={(e) => setNameSearch(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId='permissionFilter'>
                            <Form.Label className='fw-semibold'>Filter by Permission</Form.Label>
                            <div>
                                {allPermissions.map((perm) => (
                                    <Button
                                        key={perm.value}
                                        variant={permsSearch === perm.value ? 'dark' : 'outline-dark'}
                                        size='sm'
                                        className='me-1 mb-1'
                                        onClick={() => setPermsSearch(permsSearch === perm.value ? '' : perm.value)}
                                    >
                                        {perm.name}
                                    </Button>
                                ))}
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <hr />

                <h5 className='mb-3'>User List ({filteredUsers.length} found)</h5>
                {updating && (
                    <div className='d-flex align-items-center mb-3 text-info'>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Updating permissions...
                    </div>
                )}

                <div className='table-responsive'>
                    <ListGroup>
                        <ListGroup.Item className='d-flex fw-bold text-muted'>
                            <Col xs={3}>Username</Col>
                            <Col xs={2}>Actions</Col>
                            <Col xs={6}>Permissions</Col>
                        </ListGroup.Item>
                        {filteredUsers.length === 0 ? (
                            <ListGroup.Item>No users match your search criteria.</ListGroup.Item>
                        ) : (
                            filteredUsers.map((user) => (
                                <ListGroup.Item key={user.user_id} className='d-flex align-items-center py-3'>
                                    <Col xs={3}>
                                        {user.username} {user.user_id === currentUser.user_id && <small className='text-primary'>(You)</small>}
                                    </Col>
                                    <Col xs={2}>
                                        <Button
                                            variant='outline-danger'
                                            size='sm'
                                            onClick={() => deleteUser(user.user_id)}
                                            disabled={
                                                user.permissions.includes('a') ||
                                                user.permissions.includes('o') ||
                                                user.user_id === currentUser.user_id ||
                                                (!isOwnerView && (user.permissions.includes('a') || user.permissions.includes('o')))
                                            }
                                        >
                                            Delete
                                        </Button>
                                    </Col>
                                    <Col xs={6} className='d-flex flex-wrap'>
                                        {allPermissions.map((perm) => (
                                            <PermissionToggleButton
                                                key={perm.value}
                                                name={perm.name}
                                                value={perm.value}
                                                userPermissions={user.permissions}
                                                targetUserId={user.user_id}
                                                currentUser={currentUser}
                                                isOwnerView={isOwnerView}
                                                onToggle={handlePermissionToggle}
                                            />
                                        ))}
                                    </Col>
                                    <Col xs={1}>
                                        <Button
                                            variant='outline-success'
                                            size='sm'
                                            onClick={() => { setShowDecks({ userId: user.user_id, username: user.username }) }}
                                        >
                                            View More
                                        </Button>
                                    </Col>
                                </ListGroup.Item>
                            ))
                        )}
                    </ListGroup>
                </div>
            </Card.Body>
        </Card>
    );
}

function ShowDecksPanel({ userData, setShowDecks }: { userData: { userId: string, username: string }, setShowDecks: Function }) {

    const [deckList, setDeckList] = useState<DeckData[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<null | string>(null);

    const [userId, setUserId] = useState(userData.userId)
    const [showCopiedToast, setShowCopiedToast] = useState(false);

    const getDeckList = useCallback(() => {
        setLoading(true);
        api
            .post('/decks/get', { userId: userId }, { withCredentials: true })
            .then((res) => {
                setDeckList(res.data);
                setErr(null);
            })
            .catch(() => {
                window.location.href = '/logout';
            })
            .finally(() => setLoading(false));
    }, [userId]);

    useEffect(() => {
        getDeckList();
    }, [getDeckList, userId]);

    return <Card>
        {showCopiedToast && (
            <div className="toast show position-fixed top-0 end-0 m-3" style={{ zIndex: 1050 }}>
                <div className="toast-body">Copied</div>
            </div>
        )}
        <Card.Header
            style={{
                position: "relative",
                backgroundColor: "#a7a7a7"
            }}
        >
            <h2>
                <span style={{ color: "#7e4545" }}>({userData.userId})</span> <span style={{ color: "#12742b" }}>{userData.username}</span>
                <Button
                    variant='outline-danger'
                    size='sm'
                    style={{ position: "absolute", right: "10px" }}
                    onClick={() => { setShowDecks(null) }}
                >
                    X
                </Button>
            </h2>
        </Card.Header>
        <Card.Body>
            <Row>
                {loading ? <Spinner animation="border" />
                    : err ? (
                        <Alert variant="danger" className="my-3">
                            {err}
                        </Alert>
                    ) : (
                        deckList.map(deckHeader =>
                            <DeckRow
                                deck_id={deckHeader.deck_id}
                                deck_name={deckHeader.deck_name}
                                deleteDeck={() => { }}
                                editable={false}
                                setShowCopiedToast={setShowCopiedToast}
                            />
                        )
                    )
                }
            </Row>
            <Row>
                <MatchList setUserId={setUserId} userId={Number(userId)} />
            </Row>
        </Card.Body>
    </Card>
}